import React, { useState, useEffect } from "react";
import { DndProvider } from "react-dnd";
import Backend from "react-dnd-html5-backend";
import { useDispatch, useStore } from "react-redux";
import socketIOClient from "socket.io-client";
import axios from "axios";

import URLs from "./utils/urls";

// Components
import Header from "./components/Header";
import KitsAndObjects from "./components/KitsAndObjects";
import FilesLibrary from "./components/FilesLibrary";
import SimCompact from "./components/SimCompact";
import {
  updateDragStatus,
  addMessage,
  addMessagesList,
  updateYear,
  restoreFormStatus,
  updateFormStatus,
  removeFile,
  updateCounter,
  updateCapital,
  addToken,
  removeToken,
  removeCoin,
  removeCoinFieldType,
  addCoins,
  addCoinsFieldType,
  updateCoins,
  updateCoinsFieldType,
  addCard,
  rentFactory,
  purchaseFactory,
  updateTeamTable,
  removePin,
  removeStartKit,
  decreaseStarterKit,
  increaseStarterKit,
  increaseStartKitCoin,
  decreaseStartKitCoin,
  increaseStartKitToken,
  decreaseStartKitToken,
  increaseStartKitBlackToken,
  decreaseStartKitBlackToken,
  increaseStartKitPin,
  decreaseStartKitPin,
  increaseStartKitFactory,
  decreaseStartKitFactory,
  increaseStartKitMachine,
  decreaseStartKitMachine,
  decreaseStartKitProductCard,
  updateStartKitProduction,
  updateFormOption,
  addSmile,
  removeSmileFace,
} from "./Redux/dispatchers";

export default function AppProvider(props) {
  const [connectionState, setConnectionState] = useState("connecting");
  const [canEdit, setCanEdit] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [zoomValue, setZoomValue] = useState(100);

  // const [languages, setLanguages] = useState([]);
  const dispatch = useDispatch();
  const store = useStore();
  useEffect(() => {
    setCanEdit(props.canEdit);
  }, [props.canEdit]);
  useEffect(() => {
    setIsAdmin(props.isAdmin);
  }, [props.isAdmin]);

  useEffect(() => {
    setTeamName(props.teamName);
  }, [props.teamName]);
  useEffect(() => {
    const socket = socketIOClient(URLs.socketEndpoint, {
      path: process.env.REACT_APP_SOCKET_PATH,
      autoConnect: false,
      transports: ["websocket", "polling"],
    });
    const listeners = [];
    const on = (event, handler) => {
      const scopedHandler = (data) => {
        if (data && typeof data === "object" && !Array.isArray(data)) {
          if (data.sessionId !== undefined && Number(data.sessionId) !== Number(window.sessionId)) return;
          if (data.teamId !== undefined && Number(data.teamId) !== 0 && Number(data.teamId) !== Number(window.teamId)) return;
        }
        handler(data);
      };
      listeners.push([event, scopedHandler]);
      socket.on(event, scopedHandler);
    };
    let disposed = false;
    let checklistVersion = 0;
    let checklistController;
    const fetchChecklist = async () => {
      const version = ++checklistVersion;
      if (checklistController) checklistController.abort();
      checklistController = new AbortController();
      try {
        const response = await axios.post(URLs.checklist, {
          sessionId: window.sessionId,
          languageId: localStorage.langId,
        }, { signal: checklistController.signal });
        if (!disposed && version === checklistVersion && response.data.success) {
          setChecklist(response.data.checklist);
        }
      } catch (error) {
        if (!disposed && version === checklistVersion && error.code !== "ERR_CANCELED") {
          console.error("SimCompact checklist synchronization failed:", error.message);
        }
      }
    };
    let timerVersion = 0;
    const restoreLegacyTimer = async () => {
      const version = ++timerVersion;
      try {
        const response = await axios.post(URLs.activeCounter, { id: window.sessionId });
        if (!disposed && version === timerVersion) {
          dispatch(updateCounter(response.data.data || { duration: 0, createdAt: 0 }));
        }
      } catch (error) {
        if (!disposed) console.error("SimCompact timer synchronization failed:", error.message);
      }
    };
    let syncPending = false;
    let isReady = false;
    const synchronize = () => {
      if (!socket.connected || syncPending) return;
      syncPending = true;
      isReady = false;
      setConnectionState("syncing");
      socket.emit("user-data", {
        user_id: window.playerId,
        team_id: window.teamId,
        session_id: window.sessionId,
        token: window.token,
      });
    };
    // Legacy board components only use emit. Gate that command boundary so
    // Socket.IO cannot buffer stale actions while offline or restoring state.
    const commands = {
      emit: (event, data) => {
        if (event === "user-data") return synchronize();
        if (socket.connected && isReady) socket.emit(event, data);
      },
    };
    window.socket = commands;
      on("connect", () => {
        syncPending = false;
        synchronize();
      });
      on("disconnect", () => {
        isReady = false;
        syncPending = false;
        setConnectionState("disconnected");
      });
      on("connect_error", (error) => {
        setConnectionState("disconnected");
        console.error("SimCompact connection failed:", error.message);
      });
      on("sync-complete", (data) => {
        isReady = true;
        if (data.timerKnown === false) restoreLegacyTimer();
        syncPending = false;
        setConnectionState("ready");
      });
      on("sync-error", (data) => {
        isReady = false;
        syncPending = false;
        setConnectionState("error");
        console.error("SimCompact synchronization failed:", data.event);
      });

      on("item-dragging", (data) => {
        if (
          parseInt(data.teamId, 10) === parseInt(window.teamId, 10) ||
          parseInt(data.teamId, 10) === 0
        ) {
          dispatch(
            updateDragStatus(data.alias, data.type, data.isDragging, data)
          );
        }
      });
      on("capital-editing", (data) => {
        if (
          parseInt(data.teamId, 10) === parseInt(window.teamId, 10) ||
          parseInt(data.teamId, 10) === 0
        ) {
          dispatch({
            type: "UPDATE_CAPITAL_STATUS",
            itemType: data.type,
            data: data,
            isDragging: data.isDragging,
          });
        }
      });

      on("add-message", (data) => {
        dispatch(addMessage(data));
      });

      on("messages-list", (data) => {
        dispatch(addMessagesList(data));
      });

      on("change-team", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch({ type: "UPDATE_TEAM_NAME", value: data.value });
        }
        //dispatch(updateTeamName(data));
      });

      on("change-year", (data) => {
        dispatch(updateYear(data));
      });

      on("restore-form-status", (data) => {
        dispatch(restoreFormStatus(data));
      });

      on("form-status", (data) => {
        dispatch(updateFormStatus(data));
      });

      on("files-list", (data) => {
        dispatch({ type: "UPDATE_FILES", value: data });
        //dispatch(updateFiles(data));
      });

      on("add-file", (data) => {
        if (
          parseInt(data.teamId) === parseInt(window.teamId) ||
          parseInt(data.teamId) === 0
        ) {
          dispatch({ type: "ADD_FILE", value: data });
        }
      });

      on("remove-file", (data) => {
        if (
          parseInt(data.teamId) === parseInt(window.teamId) ||
          parseInt(data.teamId) === 0
        ) {
          dispatch(removeFile(data._id));
        }
      });

      on("add-timer", (data) => {
        timerVersion++;
        dispatch(updateCounter(data.data));
      });

      on("remove-timer", (data) => {
        timerVersion++;
        dispatch(
          updateCounter({
            duration: 0,
            createdAt: 0,
          })
        );
      });

      on("restore-session", (data) => {
        dispatch({ type: "UPDATE_STATE", data: data });
        //dispatch(updateState(data));
        if (data.activeForm !== undefined) {
          dispatch(restoreFormStatus(data.activeForm));
        }
      });

      on("update-capital", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(updateCapital(data.value));
        }
      });

      on("add-token", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(addToken(data.type, data.value, data.droppedTokenType));
        }
      });

      on("remove-token", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(removeToken(data.type, data.value));
        }
      });

      on("add-container", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch({ type: "ADD_CONTAINER", data: data });
        }
      });

      on("remove-container", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch({ type: "REMOVE_CONTAINER", data: data });
        }
      });

      on("add-coin", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch({ type: "ADD_COIN", data: data });
        }
      });

      on("remove-coin", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          if (data.fieldType === undefined) {
            dispatch(
              removeCoin(
                data.alias,
                data.id,
                data.index,
                data.playerId,
                data.playerName
              )
            );
          } else {
            dispatch(
              removeCoinFieldType(
                data.alias,
                data.fieldType,
                data.index,
                data.id,
                data.playerId,
                data.playerName
              )
            );
          }
        }
      });

      on("add-coins", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          if (data.fieldType === undefined) {
            dispatch(
              addCoins(
                data.alias,
                data.id,
                data.coins,
                data.playerId,
                data.playerName
              )
            );
          } else {
            dispatch(
              addCoinsFieldType(
                data.alias,
                data.fieldType,
                data.coins,
                data.id,
                data.playerId,
                data.playerName
              )
            );
          }
        }
      });

      on("update-coins", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          if (data.fieldType === undefined) {
            dispatch(
              updateCoins(
                data.alias,
                data.id,
                data.coins,
                data.playerId,
                data.playerName
              )
            );
          } else {
            dispatch(
              updateCoinsFieldType(
                data.alias,
                data.fieldType,
                data.coins,
                data.id,
                data.playerId,
                data.playerName
              )
            );
          }
        }
      });

      on("add-card", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(
            addCard(data.alias, data.optionType, data.id, data.value, data.color)
          );
        }
      });

      on("rent-factory", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(rentFactory(data.id));
        }
      });

      on("purchase-factory", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(purchaseFactory(data.id));
        }
      });

      on("update-team-table", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(updateTeamTable(data));
        }
      });

      on("add-pin", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch({ type: "ADD_PIN", alias: data.alias, id: data.id, title: data.title, speed: data.speed });
        }
      });

      on("add-smile-face", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(addSmile(data.alias, data.id, data.number));
        }
      });

      on("remove-pin", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(removePin(data.alias, data.title, data.speed, data.id));
        }
      });

      on("remove-smile-face", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(removeSmileFace(data.alias, data.id, data.number));
        }
      });

      on("remove-starter-kit", (data) => {
        dispatch(removeStartKit(data.id));
      });

      on("decrease-starter-kit", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(decreaseStarterKit(data));
        }
      });

      on("increase-starter-kit", (data) => {
        if (!data.teamId || parseInt(data.teamId, 10) === parseInt(window.teamId, 10) || parseInt(data.teamId, 10) === 0) {
          dispatch(increaseStarterKit(data));
        }
      });

      on("add-starter-kit-coin", (data) => {
        dispatch(increaseStartKitCoin(data.value));
      });

      on("remove-starter-kit-coin", (data) => {
        dispatch(decreaseStartKitCoin(data.value));
      });

      on("add-starter-kit-token", (data) => {
        dispatch(increaseStartKitToken(data.value));
      });

      on("remove-starter-kit-token", (data) => {
        dispatch(decreaseStartKitToken(data.value));
      });

      on("add-starter-kit-black-token", (data) => {
        dispatch(increaseStartKitBlackToken(data.value));
      });

      on("remove-starter-kit-black-token", (data) => {
        dispatch(decreaseStartKitBlackToken(data.value));
      });

      on("add-starter-kit-pin", (data) => {
        dispatch(increaseStartKitPin(data.count));
      });

      on("remove-starter-kit-pin", (data) => {
        dispatch(decreaseStartKitPin(data.count));
      });

      on("add-starter-kit-factory", (data) => {
        dispatch(increaseStartKitFactory(data.count));
      });

      on("remove-starter-kit-factory", (data) => {
        dispatch(decreaseStartKitFactory(data.count));
      });

      on("add-starter-kit-machine", (data) => {
        dispatch(increaseStartKitMachine(data.count));
      });

      on("remove-starter-kit-machine", (data) => {
        dispatch(decreaseStartKitMachine(data.count));
      });

      on("remove-starter-kit-production", (data) => {
        dispatch(decreaseStartKitProductCard(data.value, data.color));
      });

      on("update-starter-kit-production", (data) => {
        dispatch(updateStartKitProduction(data.value));
      });

      on("reset-form", (data) => {
        if (data.year !== undefined && Number(data.year) !== Number(store.getState().session.formSelectedYear)) return;
        dispatch({ type: "UPDATE_FORM", form: data.type, value: data.value });
      });

      on("update-form", (data) => {
        const session = store.getState().session;
        if (Number(data.year === undefined ? session.year : data.year) === Number(session.formSelectedYear)) {
          if (data.option !== undefined) {
            dispatch(
              updateFormOption(
                data.type,
                data.section,
                data.field,
                data.option,
                data.value
              )
            );
          } else {
            dispatch(
              updateFormOption(
                data.type,
                data.section,
                data.field,
                undefined,
                data.value
              )
            );
          }
        }
      });

      on("session-status-changed", (data) => {
        checkSessionStatus(data.status);
      });

      on("change-checklist-period", () => {
        fetchChecklist();
      });
    const resume = () => {
      if (document.visibilityState === "visible") synchronize();
    };
    window.addEventListener("online", synchronize);
    document.addEventListener("visibilitychange", resume);
    socket.connect();
    fetchChecklist();
    return () => {
      disposed = true;
      if (checklistController) checklistController.abort();
      listeners.forEach(([event, handler]) => socket.off(event, handler));
      window.removeEventListener("online", synchronize);
      document.removeEventListener("visibilitychange", resume);
      socket.disconnect();
      if (window.socket === commands) delete window.socket;
    };
  }, [dispatch, store]);

  const checkSessionStatus = (status) => {
    switch (parseInt(status)) {
      case 2: // Session Running
        setCanEdit(true);
        break;
      case 3: // Session Paused
        setCanEdit(false);
        break;
      default:
        break;
    }
  };

  return (
    <DndProvider backend={Backend}>
      {connectionState !== "ready" && (
        <div role="status" aria-live="polite">
          {connectionState === "error" ? "Synchronization failed. " : "Synchronizing simulation… "}
          {connectionState === "error" && <button onClick={() => window.socket?.emit("user-data", {
            user_id: window.playerId, team_id: window.teamId,
            session_id: window.sessionId, token: window.token,
          })}>Retry</button>}
        </div>
      )}
      <Header
        canEdit={canEdit && connectionState === "ready"}
        isAdmin={isAdmin}
        logoutAction={props.logout}
        teamName={teamName}
        zoomValue={zoomValue}
        setZoomValue={setZoomValue}
        languages={props.languages}
      />
      <KitsAndObjects canEdit={canEdit && connectionState === "ready"} />
      <div className={`main-app-bg zoom${zoomValue}`}>
        <SimCompact canEdit={canEdit && connectionState === "ready"} />
      </div>
      <FilesLibrary canEdit={canEdit && connectionState === "ready"} checklist={checklist} />
    </DndProvider>
  );
}
